import { useEffect, useState } from 'react'
import { ChevronLeft } from 'react-feather'
import { queries } from '../../../../../api'
import { ChatType } from '../../../../../constants'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import { selSelectedUserProfile } from '../../../../../redux/reducers/messenger/chatSlice'
import {
  setMobileMenu,
  setProfileSidebarWidth,
  setProfileToggle,
} from '../../../../../redux/reducers/messenger/messengerSlice'
import { SvgIcon } from '../../../../../shared/icons'
import ChatAvatar from '../../../../../shared/image/ChatAvatar'
import { usePresenceStatus } from '../../../../../hooks/usePresenceStatus'
import { getChatId } from '../../../../../utils'
import { presenceDotClass } from '../../../../../utils/presence'
import { getShadowRoot } from '../../../../../Webcomponent/utils/common'
import { useProfileSidebarEffect } from '../../../../../utils/useSidebarEffects'
import BroadcastInfoModal from './broadcast/BroadcastInfoModal'
import Contact from './contact'
import GroupInfoModal from './group/InfoModal'
import Search from './Search'
import './../../../../../utils/prototype'

const ChatHeader = () => {
  const [search, setSearch] = useState(false)
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { selectedUser, selectedUserProfile } = useAppSelector((state) => state.chat)
  const { app_name } = useAppSelector((state) => state.settings)
  const { profileToggle, profileSidebarWidth } = useAppSelector((state) => state.messenger)
  const isGroupChat = selectedUser?.chat_type === 'group'
  const isBroadcastChat = selectedUser?.chat_type === 'broadcast' || selectedUser?.isBroadcast
  const [broadcastMember, setBroadcastMember] = useState<string[]>([])
  const chatId = getChatId(selectedUser)

  const { data: membersData } = queries.useGetGroupMembers(
    isGroupChat ? chatId : undefined,
    { page: 1, limit: 1 },
    { enabled: isGroupChat && !!chatId },
  )

  const { data: userProfileData, isPlaceholderData: isProfilePlaceholder } = queries.useGetUserProfile(
    isGroupChat ? undefined : chatId,
    {
      enabled:
        !!chatId && !selectedUser?.isBroadcast && !selectedUser?.isGroup && selectedUser?.chat_type !== ChatType.group,
    },
  )

  // While switching conversations, react-query keeps the PREVIOUS user's profile
  // as placeholder data (see useApiGet). Ignoring it ensures the header's avatar,
  // verified badge and last-seen never belong to the previously selected user —
  // we fall back to `selectedUser` (already the new user) until the real profile
  // for this chat loads.
  const userProfile = isProfilePlaceholder ? undefined : userProfileData

  useEffect(() => {
    const list = selectedUser?.recipients?.map((item) => item.name)
    setBroadcastMember(list?.slice(0, 3) || [])
  }, [selectedUser])

  useEffect(() => {
    dispatch(selSelectedUserProfile(userProfile))
  }, [userProfile])

  const presenceView = usePresenceStatus(chatId && !isGroupChat ? chatId : null)
  const memberCount = membersData?.total_members || selectedUser?.memberCount || 0

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateSize = () => dispatch(setProfileSidebarWidth(window.innerWidth))
      window.addEventListener('resize', updateSize)
      updateSize()
      return () => window.removeEventListener('resize', updateSize)
    }
  }, [dispatch])

  useProfileSidebarEffect(profileToggle, profileSidebarWidth)

  const handleProfileToggle = () => {
    dispatch(setProfileToggle(!profileToggle))
  }

  const handleMobileMenu = () => {
    // Always go back to the chat list. We reset the state AND remove the class
    // directly: `mobileMenu` can drift out of sync with the actual `.mobile-menu`
    // class, and a plain toggle would then just re-sync the state on the first
    // tap (no visual change) and require a second tap to actually go back.
    dispatch(setMobileMenu(false))
    getShadowRoot()?.querySelector('.sidebar-toggle')?.classList.remove('mobile-menu')
  }

  // The server already withholds the time for users who hide their last seen.
  // An unknown status (no data, or the socket is down) shows no subtitle.
  const memberSubtitle = isGroupChat
    ? `${memberCount} member${memberCount === 1 ? '' : 's'}`
    : !presenceView
      ? ''
      : presenceView.lastSeenText
        ? `${presenceView.label} · ${presenceView.lastSeenText}`
        : presenceView.label

  const broadcastSubtitle = () => {
    if (!broadcastMember || broadcastMember.length === 0) return 'No recipients'

    const names = broadcastMember.join(', ')
    const hasMore = selectedUser?.recipients && selectedUser.recipients.length > 3

    return hasMore ? `${names}...` : names
  }

  const handleGroupHeaderClick = () => {
    if (isGroupChat) {
      setGroupModalOpen(true)
    } else if (isBroadcastChat) {
      setBroadcastModalOpen(true)
    } else {
      handleProfileToggle()
    }
  }

  if (!selectedUser) {
    return null
  }

  return (
    <div className="contact-details d-flex justify-content-between">
      <Search search={search} setSearch={setSearch} />
      <div>
        <div className="d-flex left gap-2">
          <div className="media-right">
            <ul>
              <li>
                <button
                  className="icon-btn bg-light-primary button-effect mobile-sidebar"
                  onClick={handleMobileMenu}
                  type="button"
                >
                  <ChevronLeft />
                </button>
              </li>
            </ul>
          </div>
          <div className="media-left cursor-pointer" onClick={handleGroupHeaderClick}>
            {!selectedUser.isBroadcast ? (
              <div
                className={`profile ${
                  !selectedUser.isAnnouncement && (isGroupChat ? 'group' : presenceDotClass(presenceView))
                } menu-trigger`}
              >
                <ChatAvatar
                  // Remount per conversation so the previous user's image is
                  // dropped immediately (instead of lingering on the reused <img>
                  // until the new one decodes) when switching chats.
                  key={selectedUser?.chat_id}
                  data={{
                    avatar: isGroupChat ? selectedUser?.avatar : userProfile?.avatar || selectedUser?.avatar,
                  }}
                  name={{ name: selectedUser.isAnnouncement ? app_name : selectedUser.name }}
                  customClass="user-info avatar-sm"
                />
              </div>
            ) : (
              <div className={`profile chatavtar-div d-flex justify-content-center align-items-center main-broadcast`}>
                <SvgIcon iconId="broadcast" />
              </div>
            )}
          </div>
          <div className="flex-grow-1 cursor-pointer" onClick={handleGroupHeaderClick}>
            <div className="d-flex align-items-center">
              <h5>{selectedUser.isAnnouncement ? app_name : selectedUser?.name?.firstTwoWords() || 'Friend'}</h5>
              {(selectedUser.isAnnouncement ||
                (!isGroupChat && (selectedUser?.is_verified || userProfile?.is_verified))) && (
                <SvgIcon iconId="blue-tick" className="ms-1 verified-tick" />
              )}
            </div>
            <p>
              {selectedUser.isBroadcast
                ? broadcastSubtitle()
                : selectedUser.isAnnouncement
                  ? selectedUserProfile?.bio || 'Announce'
                  : memberSubtitle}
            </p>
          </div>
        </div>
      </div>
      <Contact search={search} setSearch={setSearch} />
      {isGroupChat && (
        <GroupInfoModal
          isOpen={groupModalOpen}
          groupId={selectedUser?.id || selectedUser.chat_id}
          onClose={() => setGroupModalOpen(false)}
        />
      )}
      {isBroadcastChat && (
        <BroadcastInfoModal
          isOpen={broadcastModalOpen}
          broadcastId={selectedUser?.chat_id || selectedUser?.id}
          onClose={() => setBroadcastModalOpen(false)}
        />
      )}
    </div>
  )
}

export default ChatHeader
